import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

/**
 * Shared "draggable marquee" behavior: a track of duplicated content that
 * auto-scrolls left at a steady speed and can be grabbed/dragged with a mouse
 * or finger. Pauses on hover and while off-screen, and honors reduced motion
 * (callers render a plain scrollable fallback when `prefersReducedMotion`).
 *
 * Render two back-to-back copies of the content inside `trackRef`; the hook
 * wraps the offset by half the track width for a seamless loop. Spread
 * `handlers` onto the `containerRef` element.
 */
export const useDragMarquee = (speedPxPerSec = 35) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);
  const visibleRef = useRef(true);
  const lastPointerXRef = useRef(0);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    setPrefersReducedMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    );
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const node = containerRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (prefersReducedMotion) return;
    let rafId = 0;
    let lastTime = 0;

    const tick = (now: number) => {
      if (!lastTime) lastTime = now;
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      if (!pausedRef.current && !draggingRef.current && visibleRef.current) {
        offsetRef.current -= speedPxPerSec * dt;
      }

      const track = trackRef.current;
      if (track) {
        const half = track.scrollWidth / 2;
        if (half > 0) {
          while (offsetRef.current <= -half) offsetRef.current += half;
          while (offsetRef.current > 0) offsetRef.current -= half;
        }
        track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [prefersReducedMotion, speedPxPerSec]);

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  const handlers = {
    onMouseEnter: () => {
      pausedRef.current = true;
    },
    onMouseLeave: () => {
      pausedRef.current = false;
    },
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = true;
      lastPointerXRef.current = e.clientX;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // some browsers reject capture on non-primary pointers; safe to ignore
      }
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - lastPointerXRef.current;
      lastPointerXRef.current = e.clientX;
      offsetRef.current += dx;
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };

  return { containerRef, trackRef, prefersReducedMotion, handlers };
};
